// Issue #104: 「中身（内容物）から探す」. A priority sorter, not a filter.
// - Series whose card carries the selected use id move to the head of their own grid;
//   matching / non-matching keep their original order (stable partition). Nothing is hidden.
// - The width / capacity finder owns `hidden`, the match-row wrapper and its product panel.
//   This script never writes `hidden` and never splits a card from its wrapper.
// - Labels come from the server-rendered buttons (the dictionary lives only in the snippet).

function pnxUseMoveUnit(card, grid) {
  const parent = card.parentElement;
  if (parent === grid) return card;
  // The width finder wraps a card and its product panel while a width is selected. Move them together.
  if (parent && parent.classList.contains('pnx-vfilter__match-row') && parent.parentElement === grid) return parent;
  return null;
}

function pnxUseIsShown(card, section) {
  for (let node = card; node && node !== section; node = node.parentElement) {
    if (node.hidden) return false;
  }
  return true;
}

function initUseFinder(root) {
  if (!(root instanceof HTMLElement) || root.dataset.pnxUseReady === 'use-v1') return;
  const scope = root.dataset.pnxUseScope || '';
  const section = root.closest('[id^="shopify-section-"]');
  const buttons = [...root.querySelectorAll('button[data-pnx-use-value]')];
  const reset = root.querySelector('[data-pnx-use-reset]');
  const result = root.querySelector('[data-pnx-use-result]');
  if (!scope || !section || !buttons.length || !reset || !result) return;

  // Original series order per grid. The card is the record; its current move unit is resolved every time.
  const grids = new Map();
  const models = [];
  section.querySelectorAll('.pnx-sgrid__card[data-pnx-use-scope]').forEach((card) => {
    if (card.dataset.pnxUseScope !== scope) return;
    const grid = card.closest('.pnx-sgrid__grid');
    if (!grid) return;
    const model = {
      card,
      tags: new Set((card.dataset.pnxUseTags || '').split(/\s+/).filter(Boolean)),
      badge: null
    };
    if (!grids.has(grid)) grids.set(grid, []);
    grids.get(grid).push(model);
    models.push(model);
  });
  if (!models.length) return;

  let selected = null;
  let selectedLabel = '';

  function arrange() {
    grids.forEach((list, grid) => {
      const ordered = selected === null ? list
        : [...list.filter((model) => model.tags.has(selected)), ...list.filter((model) => !model.tags.has(selected))];
      const units = ordered.map((model) => pnxUseMoveUnit(model.card, grid));
      // Unknown DOM shape: leave this grid exactly as it is.
      if (units.some((unit) => !unit)) return;
      const wanted = new Set(units);
      const current = [...grid.children].filter((child) => wanted.has(child));
      if (current.length === units.length && current.every((child, i) => child === units[i])) return;
      const fragment = document.createDocumentFragment();
      units.forEach((unit) => fragment.append(unit));
      grid.append(fragment);
    });
  }

  function badges() {
    models.forEach((model) => {
      if (model.badge) { model.badge.remove(); model.badge = null; }
      if (selected === null || !model.tags.has(selected)) return;
      const cta = model.card.querySelector('.pnx-sgrid__collection-cta');
      if (!cta) return;
      const badge = document.createElement('span');
      badge.className = 'pnx-usefinder__badge';
      badge.textContent = `用途例：${selectedLabel}`;
      cta.before(badge);
      model.badge = badge;
    });
  }

  function announce() {
    let text = '';
    if (selected !== null) {
      const shown = models.filter((model) => model.tags.has(selected) && pnxUseIsShown(model.card, section)).length;
      text = shown
        ? `用途例「${selectedLabel}」：該当する${shown}シリーズを先頭に表示しています。`
        : `用途例「${selectedLabel}」：表示中のシリーズに該当はありません。`;
    }
    if (result.textContent !== text) result.textContent = text;
  }

  function select(value, label) {
    selected = value;
    selectedLabel = value === null ? '' : label;
    buttons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.pnxUseValue === selected)));
    reset.setAttribute('aria-disabled', String(selected === null));
    arrange();
    badges();
    announce();
  }

  buttons.forEach((button) => {
    button.addEventListener('click', () => {
      const value = button.dataset.pnxUseValue;
      if (value === selected) select(null, '');
      else select(value, button.textContent.trim());
    });
  });
  reset.addEventListener('click', () => {
    if (selected !== null) select(null, '');
  });

  // Read-only: the width / capacity finder toggles `hidden` and wraps cards. Only the result text follows it.
  const observer = new MutationObserver(() => { if (selected !== null) announce(); });
  new Set([...grids.keys()].map((grid) => grid.closest('.pnx-sgrid') || grid)).forEach((group) => {
    observer.observe(group, { attributes: true, attributeFilter: ['hidden'], childList: true, subtree: true });
  });

  reset.setAttribute('aria-disabled', 'true');
  reset.disabled = false;
  buttons.forEach((button) => { button.disabled = false; });
  root.dataset.pnxUseReady = 'use-v1';
}

function initUseFinders(scope = document) {
  scope.querySelectorAll?.('[data-pnx-use-finder]').forEach((root) => {
    try { initUseFinder(root); } catch (_) { /* Leave the server order and disabled controls as they are. */ }
  });
}

initUseFinders(document);
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) initUseFinders(event.target);
});
